import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/moderator/Sidebar';
import config from '../config/config';
import { Navbar } from '../components/Navbar';

export function ModeratorDashboard() {
  const [isModerator, setIsModerator] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [animalStats, setAnimalStats] = useState([]);
  const [uniqueAnimals, setUniqueAnimals] = useState([]);
  const [financialSummary, setFinancialSummary] = useState({
    total_received: 0,
    total_expense: 0,
    net_balance: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');

    if (!token || role !== 'moderator') {
      navigate('/');
      return;
    }

    const verifyModerator = async () => {
      try {
        const response = await fetch(`${config.baseUrl}/api/auth/check-moderator`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        if (response.ok && data.isModerator) {
          setIsModerator(true);
        } else {
          navigate('/');
        }
      } catch (error) {
        console.error('Verification error:', error);
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    verifyModerator();
  }, [navigate]);

  useEffect(() => {
    const fetchAnimalStats = async () => {
      try {
        const response = await fetch(`${config.baseUrl}/api/dashboard/animal-stats`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        setAnimalStats(data);
      } catch (error) {
        console.error('Error fetching animal statistics:', error);
      }
    };

    fetchAnimalStats();
  }, []);

  useEffect(() => {
    const fetchUniqueAnimals = async () => {
      try {
        const response = await fetch(`${config.baseUrl}/api/dashboard/unique-animals`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        setUniqueAnimals(data);
      } catch (error) {
        console.error('Error fetching unique animals:', error);
      }
    };

    fetchUniqueAnimals();
  }, []);

  useEffect(() => {
    const fetchFinancialSummary = async () => {
      try {
        const response = await fetch(`${config.baseUrl}/api/dashboard/financial-summary`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        console.log(data);
        setFinancialSummary(data);
      } catch (error) {
        console.error('Error fetching financial summary:', error);
      }
    };

    fetchFinancialSummary();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!isModerator) {
    return null;
  }

  return (
    <>
      <Sidebar />
      <Navbar />
      <div className="flex">
        <div className="flex-1 min-h-screen bg-gray-100 p-8">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-3xl font-bold text-center mb-8">Moderator Dashboard</h1>

            {/* Financial Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-600 mb-2">Today's Revenue</h3>
                <p className="text-2xl font-bold text-green-600">
                  Rs. {financialSummary.total_received?.toLocaleString() || 0}
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-600 mb-2">Today's Expenses</h3>
                <p className="text-2xl font-bold text-red-600">
                  Rs. {financialSummary.total_expense?.toLocaleString() || 0}
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-600 mb-2">Net Balance</h3>
                <p className={`text-2xl font-bold ${financialSummary.net_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  Rs. {financialSummary.net_balance?.toLocaleString() || 0}
                </p>
              </div>
            </div>

            {/* Animal Statistics Table */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <h2 className="text-xl font-semibold mb-4">Summary</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Animal Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day 1</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day 2</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day 3</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Animals</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {animalStats.map((stat, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap capitalize">{stat.animal_type}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {stat.category === 'A' ? 'Ijtimai' :
                            stat.category === 'B' ? 'Asaan' :
                              stat.category === 'C' ? 'Super Asaan' : stat.category}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{stat.day1 || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{stat.day2 || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{stat.day3 || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{stat.total_animals || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Unique Animals Table */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <h2 className="text-xl font-semibold mb-4">Day 1 Animals</h2>
              <div className="overflow-x-auto">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-2">
                    <span>Show</span>
                    <select className="border rounded p-1">
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </select>
                    <span>entries</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>Search:</span>
                    <input type="text" className="border rounded p-1 w-48" />
                  </div>
                </div>

                <table className="min-w-full table-auto">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Animal No</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining Shares</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {uniqueAnimals.map((animal, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap capitalize">{animal.animal_type}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {animal.category === 'A' ? 'Ijtimai' :
                            animal.category === 'B' ? 'Asaan' :
                              animal.category === 'C' ? 'Super Asaan' : animal.category}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{animal.animal_number}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{animal.share_count}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600">
                            Book
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>



            {/* Existing dashboard cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

            </div>
          </div>
        </div>
      </div>
    </>
  );
}